import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    ArrowLeft,
    FileSpreadsheet,
    FileText,
    Download,
    XCircle
} from "lucide-react";

import api from "../services/api";


function ExportPage() {
    const navigate = useNavigate();

    const [permissions, setPermissions] = useState([]);
    const [userRole, setUserRole] = useState("");
    const [accessLoading, setAccessLoading] = useState(true);

    const [downloading, setDownloading] = useState("");
    const [error, setError] = useState("");


    const hasPermission = (key) => {
        if (userRole === "OWNER") {
            return true;
        }

        return permissions.some(
            item => item.permission_key === key
        );
    };


    useEffect(() => {
        const loadAccess = async () => {
            try {
                const response = await api.get("/auth/me");

                setUserRole(
                    response.data.user?.role || ""
                );

                setPermissions(
                    response.data.permissions || []
                );

            } catch (error) {
                setError(
                    "Unable to verify permissions"
                );

            } finally {
                setAccessLoading(false);
            }
        };

        loadAccess();
    }, []);


    const downloadFile = async (
        type
    ) => {
        try {
            setDownloading(type);
            setError("");

            const response = await api.get(
                `/export/products/${type}`,
                {
                    responseType: "blob"
                }
            );


            const blob =
                new Blob(
                    [response.data],
                    {
                        type:
                            type === "excel"
                                ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                : "application/pdf"
                    }
                );


            const url =
                window.URL.createObjectURL(
                    blob
                );


            const link =
                document.createElement(
                    "a"
                );


            link.href = url;

            link.download =
                type === "excel"
                    ? "products.xlsx"
                    : "products.pdf";


            document.body.appendChild(
                link
            );

            link.click();

            link.remove();


            window.URL.revokeObjectURL(
                url
            );

        } catch (error) {
            setError(
                error.response?.data?.message ||
                "Unable to export file"
            );

        } finally {
            setDownloading("");
        }
    };


    if (accessLoading) {
        return (
            <div className="mobile-app">
                <div className="loading-state">
                    Loading...
                </div>
            </div>
        );
    }


    if (!hasPermission("EXPORT_FILES")) {
        return (
            <div className="mobile-app">

                <header className="page-header">

                    <button
                        className="back-button"
                        onClick={() =>
                            navigate("/dashboard")
                        }
                    >
                        <ArrowLeft size={21} />
                    </button>

                    <div>
                        <h1>
                            Export
                        </h1>

                        <p>
                            Product files
                        </p>
                    </div>

                </header>


                <div className="permission-denied-page">

                    <XCircle size={44} />

                    <h2>
                        Access Denied
                    </h2>

                    <p>
                        You do not have permission
                        to export product files.
                    </p>

                </div>

            </div>
        );
    }


    return (
        <div className="mobile-app">

            <header className="page-header">

                <button
                    className="back-button"
                    onClick={() =>
                        navigate("/dashboard")
                    }
                >
                    <ArrowLeft size={21} />
                </button>

                <div>
                    <h1>
                        Export Products
                    </h1>

                    <p>
                        Download price lists
                    </p>
                </div>

            </header>


            <main className="export-content">

                <section className="export-intro">

                    <h2>
                        Choose Export Format
                    </h2>

                    <p>
                        Download the current product
                        price list as Excel or PDF.
                    </p>

                </section>


                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}


                <button
                    className="export-card"
                    onClick={() =>
                        downloadFile("excel")
                    }
                    disabled={
                        downloading !== ""
                    }
                >

                    <div className="export-icon excel-export-icon">
                        <FileSpreadsheet
                            size={27}
                        />
                    </div>


                    <div className="export-card-info">

                        <strong>
                            Export Excel
                        </strong>

                        <small>
                            Download editable
                            .xlsx price list
                        </small>

                    </div>


                    <div className="export-download-icon">
                        <Download
                            size={20}
                        />
                    </div>


                    {downloading === "excel" && (
                        <span className="export-status">
                            Downloading...
                        </span>
                    )}

                </button>


                <button
                    className="export-card"
                    onClick={() =>
                        downloadFile("pdf")
                    }
                    disabled={
                        downloading !== ""
                    }
                >

                    <div className="export-icon pdf-export-icon">
                        <FileText
                            size={27}
                        />
                    </div>


                    <div className="export-card-info">

                        <strong>
                            Export PDF
                        </strong>

                        <small>
                            Download printable
                            price list
                        </small>

                    </div>


                    <div className="export-download-icon">
                        <Download
                            size={20}
                        />
                    </div>


                    {downloading === "pdf" && (
                        <span className="export-status">
                            Downloading...
                        </span>
                    )}

                </button>

            </main>

        </div>
    );
}


export default ExportPage;